// 3D edges from a line seen in several photos: each photo's segment and its camera centre span a plane the edge lies
// in, so the edge is where those planes meet (least squares: its direction is the one all their normals are square
// to). Its ends are where the photos' end rays come closest to it (the stretch every photo sees). When the planes
// are nearly the same (the line runs along the cameras' baseline) that is ill-posed, and the depth along the
// segment gives the edge instead. cluster merges pieces of one edge; near finds points within reach of the edges.
import { eigSym3 } from '../edit/select.ts';
import { type PhotoCam, normOf } from './camgeom.ts';
import type { V3 } from '../math.ts';

export interface Obs { c: PhotoCam; s: ArrayLike<number> }
export interface Edge3 { A: V3; B: V3; rms: number; views: number; how: 'planes' | 'depth' }

const sub = (a: V3, b: V3): V3 => [a[0] - b[0], a[1] - b[1], a[2] - b[2]];
const dot = (a: ArrayLike<number>, b: ArrayLike<number>) => a[0] * b[0] + a[1] * b[1] + a[2] * b[2];
const cross = (a: V3, b: V3): V3 => [a[1] * b[2] - a[2] * b[1], a[2] * b[0] - a[0] * b[2], a[0] * b[1] - a[1] * b[0]];
const unit = (a: V3): V3 => { const l = Math.hypot(...a) || 1; return [a[0] / l, a[1] / l, a[2] / l]; };

/** World direction of the ray through photo pixel (u, v). */
function dirOf(c: PhotoCam, u: number, v: number): V3 {
  const [x, y] = normOf(c, u, v), R = c.P.R;
  return unit([R[0] * x + R[1] * y + R[2], R[3] * x + R[4] * y + R[5], R[6] * x + R[7] * y + R[8]]);
}

/** Along the line X0 + t d: the t of its point nearest the ray o + s e. */
function nearestT(X0: V3, d: V3, o: V3, e: V3): number {
  const w = sub(X0, o), b = dot(d, e), den = 1 - b * b;
  if (den < 1e-9) return NaN;
  return (b * dot(e, w) - dot(d, w)) / den;
}

/** Signed distance (px) of photo point (u, v) to the projection of the line A-B (null: behind the camera). */
export function lineResidual(c: PhotoCam, A: V3, B: V3, u: number, v: number, proj: (c: PhotoCam, X: V3) => [number, number, number] | null): number | null {
  const a = proj(c, A), b = proj(c, B);
  if (!a || !b) return null;
  const dx = b[0] - a[0], dy = b[1] - a[1], L = Math.hypot(dx, dy);
  if (L < 1e-9) return null;
  return (-(u - a[0]) * dy + (v - a[1]) * dx) / L;
}

/**
 * The edge seen as the segments obs (≥ 2 photos), or null; minAngle: the photos' planes must differ by this much
 * (degrees), else depth (3D points sampled along each segment: depthPts) gives it, as it does when the planes'
 * edge is well off those points.
 */
export function triangulate(obs: Obs[], depthPts?: V3[], minAngle = 4): Edge3 | null {
  if (obs.length < 2) return null;
  const planes = obs.map(({ c, s }) => {
    const n = unit(cross(dirOf(c, s[0], s[1]), dirOf(c, s[2], s[3])));
    return { n, w: -dot(n, c.P.t), o: [...c.P.t] as V3, e0: dirOf(c, s[0], s[1]), e1: dirOf(c, s[2], s[3]) };
  });
  const M = [0, 0, 0, 0, 0, 0, 0, 0, 0];
  for (const p of planes) for (let r = 0; r < 3; r++) for (let q = 0; q < 3; q++) M[3 * r + q] += p.n[r] * p.n[q];
  const { vals, vecs } = eigSym3(M), ord = [0, 1, 2].sort((a, b) => vals[a] - vals[b]);
  // the middle eigenvalue per photo: how much the planes differ (two at angle θ: (1 - cos θ) / 2)
  const spread = vals[ord[1]] / obs.length;
  if (spread < (1 - Math.cos((minAngle * Math.PI) / 180)) / 2) return depthPts && depthPts.length >= 2 ? fromDepth(depthPts, obs.length) : null;
  const d: V3 = unit([vecs[ord[0]], vecs[3 + ord[0]], vecs[6 + ord[0]]]);
  // a point on it: least squares over the planes, pinned along d at the cameras' mean
  const c0: V3 = [0, 0, 0];
  for (const p of planes) for (let k = 0; k < 3; k++) c0[k] += p.o[k] / planes.length;
  const A = M.slice(), b = [0, 0, 0];
  for (let r = 0; r < 3; r++) { for (let q = 0; q < 3; q++) A[3 * r + q] += d[r] * d[q]; b[r] = d[r] * dot(d, c0); }
  for (const p of planes) for (let r = 0; r < 3; r++) b[r] -= p.n[r] * p.w;
  const X0 = solve3(A, b);
  if (!X0) return null;
  // its ends: per photo, where its end rays come nearest; the stretch all of them see
  let lo = -Infinity, hi = Infinity;
  for (const p of planes) {
    const ta = nearestT(X0, d, p.o, p.e0), tb = nearestT(X0, d, p.o, p.e1);
    if (!Number.isFinite(ta) || !Number.isFinite(tb)) continue;
    lo = Math.max(lo, Math.min(ta, tb)); hi = Math.min(hi, Math.max(ta, tb));
  }
  if (!(hi > lo)) return null;
  const Ae: V3 = [X0[0] + lo * d[0], X0[1] + lo * d[1], X0[2] + lo * d[2]], Be: V3 = [X0[0] + hi * d[0], X0[1] + hi * d[1], X0[2] + hi * d[2]];
  // rms: of the plane distances of the ends (m)
  let ss = 0;
  for (const p of planes) ss += (dot(p.n, Ae) + p.w) ** 2 + (dot(p.n, Be) + p.w) ** 2;
  // the depth along the segments must agree (a near-degenerate set of planes, or a wrong match, puts it elsewhere)
  if (depthPts && depthPts.length >= 3) {
    let far = 0;
    for (const X of depthPts) if (segDist(X, Ae, Be) > 0.03 + 0.02 * Math.hypot(...sub(X, c0))) far++;
    if (far > depthPts.length / 3) return fromDepth(depthPts, obs.length);
  }
  return { A: Ae, B: Be, rms: Math.sqrt(ss / (2 * planes.length)), views: obs.length, how: 'planes' };
}

/** A 3D line fitted to points (principal axis), from the extreme ones. */
function fromDepth(pts: V3[], views: number): Edge3 | null {
  const n = pts.length, c: V3 = [0, 0, 0];
  for (const p of pts) for (let k = 0; k < 3; k++) c[k] += p[k] / n;
  const C = [0, 0, 0, 0, 0, 0, 0, 0, 0];
  for (const p of pts) { const q = sub(p, c); for (let r = 0; r < 3; r++) for (let s = 0; s < 3; s++) C[3 * r + s] += q[r] * q[s]; }
  const { vals, vecs } = eigSym3(C);
  let j = 0;
  for (let k = 1; k < 3; k++) if (vals[k] > vals[j]) j = k;
  const d: V3 = [vecs[j], vecs[3 + j], vecs[6 + j]];
  let lo = Infinity, hi = -Infinity, ss = 0;
  for (const p of pts) { const t = dot(sub(p, c), d); lo = Math.min(lo, t); hi = Math.max(hi, t); const r = sub(sub(p, c), [t * d[0], t * d[1], t * d[2]]); ss += dot(r, r); }
  if (!(hi > lo)) return null;
  return { A: [c[0] + lo * d[0], c[1] + lo * d[1], c[2] + lo * d[2]], B: [c[0] + hi * d[0], c[1] + hi * d[1], c[2] + hi * d[2]], rms: Math.sqrt(ss / n), views, how: 'depth' };
}

function solve3(A: number[], b: number[]): V3 | null {
  const [a, bb, c, d, e, f, g, h, i] = A, det = a * (e * i - f * h) - bb * (d * i - f * g) + c * (d * h - e * g);
  if (Math.abs(det) < 1e-12) return null;
  return [
    (b[0] * (e * i - f * h) - bb * (b[1] * i - f * b[2]) + c * (b[1] * h - e * b[2])) / det,
    (a * (b[1] * i - f * b[2]) - b[0] * (d * i - f * g) + c * (d * b[2] - b[1] * g)) / det,
    (a * (e * b[2] - b[1] * h) - bb * (d * b[2] - b[1] * g) + b[0] * (d * h - e * g)) / det,
  ];
}

/** Distance from point p to segment A-B. */
export function segDist(p: ArrayLike<number>, A: ArrayLike<number>, B: ArrayLike<number>): number {
  const dx = B[0] - A[0], dy = B[1] - A[1], dz = B[2] - A[2], L = dx * dx + dy * dy + dz * dz;
  const t = L ? Math.max(0, Math.min(1, ((p[0] - A[0]) * dx + (p[1] - A[1]) * dy + (p[2] - A[2]) * dz) / L)) : 0;
  return Math.hypot(p[0] - A[0] - t * dx, p[1] - A[1] - t * dy, p[2] - A[2] - t * dz);
}

/**
 * Merge edges that are one: directions within ang°, each's ends within d of the other's line, overlapping or
 * nearly touching. The merged edge spans both (along the longer one's line, weighted by length).
 */
export function cluster(es: Edge3[], ang = 3, d = 0.02): Edge3[] {
  const cosA = Math.cos((ang * Math.PI) / 180), out = es.map(e => ({ ...e }));
  const lineDist = (p: V3, A: V3, u: V3) => { const w = sub(p, A), t = dot(w, u); return Math.hypot(w[0] - t * u[0], w[1] - t * u[1], w[2] - t * u[2]); };
  for (let changed = true; changed;) {
    changed = false;
    out.sort((a, b) => Math.hypot(...sub(b.B, b.A)) - Math.hypot(...sub(a.B, a.A)));
    for (let i = 0; i < out.length && !changed; i++) for (let j = i + 1; j < out.length && !changed; j++) {
      const a = out[i], b = out[j], ua = unit(sub(a.B, a.A)), ub = unit(sub(b.B, b.A));
      if (Math.abs(dot(ua, ub)) < cosA) continue;
      if (lineDist(b.A, a.A, ua) > d || lineDist(b.B, a.A, ua) > d) continue;
      const La = Math.hypot(...sub(a.B, a.A)), t0 = dot(sub(b.A, a.A), ua), t1 = dot(sub(b.B, a.A), ua);
      if (Math.min(t0, t1) > La + d || Math.max(t0, t1) < -d) continue;   // apart along it
      const lo = Math.min(0, t0, t1), hi = Math.max(La, t0, t1);
      out[i] = { A: [a.A[0] + lo * ua[0], a.A[1] + lo * ua[1], a.A[2] + lo * ua[2]], B: [a.A[0] + hi * ua[0], a.A[1] + hi * ua[1], a.A[2] + hi * ua[2]],
        rms: Math.max(a.rms, b.rms), views: Math.max(a.views, b.views), how: a.how };
      out.splice(j, 1);
      changed = true;
    }
  }
  return out;
}

/** Per point of P, 1 when within r of one of the edges (packed A, B: 6 per edge). */
export function nearEdges(P: Float32Array, segs: Float32Array, r: number): Uint8Array {
  const n = P.length / 3, out = new Uint8Array(n), cell = Math.max(r, 0.01), g = new Map<number, number[]>();
  const key = (x: number, y: number, z: number) => ((Math.floor(x / cell) + 32768) * 65536 + (Math.floor(y / cell) + 32768)) * 65536 + (Math.floor(z / cell) + 32768);
  for (let e = 0; e < segs.length / 6; e++) {
    const A = segs.subarray(6 * e, 6 * e + 3), B = segs.subarray(6 * e + 3, 6 * e + 6), L = Math.hypot(B[0] - A[0], B[1] - A[1], B[2] - A[2]);
    const steps = Math.max(1, Math.ceil(L / (cell / 2)));
    const seen = new Set<number>();
    for (let s = 0; s <= steps; s++) {
      const t = s / steps, k = key(A[0] + t * (B[0] - A[0]), A[1] + t * (B[1] - A[1]), A[2] + t * (B[2] - A[2]));
      if (seen.has(k)) continue;
      seen.add(k);
      // into this cell and the 26 round it: a point then looks in its own cell only
      for (let a = -1; a <= 1; a++) for (let b = -1; b <= 1; b++) for (let c = -1; c <= 1; c++) {
        const kk = k + (a * 65536 + b) * 65536 + c, l = g.get(kk) ?? g.set(kk, []).get(kk)!;
        if (l[l.length - 1] !== e) l.push(e);
      }
    }
  }
  // only points in the edges' box (grown by r) need looking at
  const lo = [Infinity, Infinity, Infinity], hi = [-Infinity, -Infinity, -Infinity];
  for (let i = 0; i < segs.length; i++) { const k = i % 3; if (segs[i] < lo[k]) lo[k] = segs[i]; if (segs[i] > hi[k]) hi[k] = segs[i]; }
  for (let k = 0; k < 3; k++) { lo[k] -= r; hi[k] += r; }
  const q = [0, 0, 0];
  for (let i = 0; i < n; i++) {
    const x = P[3 * i], y = P[3 * i + 1], z = P[3 * i + 2];
    if (x < lo[0] || x > hi[0] || y < lo[1] || y > hi[1] || z < lo[2] || z > hi[2]) continue;
    q[0] = x; q[1] = y; q[2] = z;
    const es = g.get(key(x, y, z));
    if (es) for (const e of es) if (segDist(q, segs.subarray(6 * e, 6 * e + 3), segs.subarray(6 * e + 3, 6 * e + 6)) <= r) { out[i] = 1; break; }
  }
  return out;
}
